import clsx from "clsx";
import { type Dispatch, type ReactNode, type SetStateAction, useState } from "react";
import { DraftFields, TextField, ToggleField } from "../../web/components/fields";
import { HoldButton } from "../../web/components/Confirm";
import { FixHere } from "../../web/components/FixHere";
import { Box, DraftBar, SectionCard } from "../../web/components/ui";
import { basisLabel } from "../../shared/money";
import { useMoney } from "../../web/lib/money";
import { can, itemTax, useItemStats, usePriceListSaver } from "./data";
import { activeJobs, activeJobsText, builtNumber, ITEM_NUMBER_MAX, type MaterialRow, type PriceListData, type TagRow } from "./meta";
import { NamePreview } from "./NameTags";
import { smartName } from "./smartTags";
import { sameTags, TagPicker } from "./TagPicker";
import { splitName } from "./names";
import { itemNumberKey } from "./setup";
import { jobDataMissing } from "./StatsTab";
import { itemContext, itemProblems } from "./attention";
import { UsedIn } from "./UsedIn";

/*
 * An item's Details tab (ItemWindow.tsx): its name and tags, item number and barcode, and stock, each card saved on its own, then
 * what it's used in.
 */

/** Its item number is missing or shared (its pill in Needs attention says which). */
const hasNumberIssue = (data: PriceListData, item: MaterialRow) => itemProblems(item, itemContext(data)).some((p) => p.id === "noNumber" || p.id === "sharedNumber");

/** A product's item number: optional (only items reach the accounting software), at most ServiceM8's length as an item's. */
const productNumberCheck = (v: string) => (v.length > ITEM_NUMBER_MAX ? `At most ${ITEM_NUMBER_MAX} characters` : null);

/** An item number: required (accounting software matches invoice lines by it), and at most ServiceM8's length. */
const itemNumberCheck = (v: string) => {
    if (!v.trim()) return "Item number is required: accounting software matches invoice lines to items by it";
    return v.length > ITEM_NUMBER_MAX ? `At most ${ITEM_NUMBER_MAX} characters` : null;
};

/** Its price, to read: for someone who can't See costs (the Pricing tab, which shows its cost too, isn't there for them). */
function PriceLine({ data, item }: Readonly<{ data: PriceListData; item: MaterialRow }>) {
    const fmt = useMoney();
    return (
        <div className="space-y-1">
            <span className="block text-(length:--label-text) text-muted">Price</span>
            <p className="m-0">
                <span className="tabular-nums">{fmt.money(item.price)}</span>{" "}
                <span className="text-xs text-muted">{basisLabel(item.price_includes_taxes, itemTax(data, item).rate)}</span>
            </p>
        </div>
    );
}

export function DetailsTab(
    props: Readonly<{
        data: PriceListData;
        item: MaterialRow;
        canEdit: boolean;
        /** Its item number, even archived: restoring it is refused while another item has its number, and changing this one's is the way out. */
        canEditNumber: boolean;
        itemNumberFocus?: number;
        /** Its name is another item's too (its pill in Needs attention): why, under the field, which is highlighted; Show takes the cursor to it. */
        nameShared?: string;
        nameFocus?: number;
        /** Show's token for its name's warning (or its tags' clash). */
        nameFixToken?: number;
        /** Its Used in, scrolled to and flashed (a product's items to update pill). */
        usedInToken?: number;
        /** Its name doesn't read as its tags say (SmartNameNotice): under its name, where Show (name) brings you. */
        nameNotice?: ReactNode;
        /** Its tags clash (a pick-one set's two): along its tags' card. */
        tagsFix?: ReactNode;
        /** A product (catalogue.ts): its item number optional, its own; no name tags (they're wording for what's sold). */
        product: boolean;
        detailsDraft: DetailsDraft | null;
        onDetailsDraft: Dispatch<SetStateAction<DetailsDraft | null>>;
        /** Its tags being edited (TagPicker), or null. */
        tagsDraft: string[] | null;
        onTagsDraft: (draft: string[] | null) => void;
    }>,
) {
    const { data, item, canEdit, itemNumberFocus } = props;
    // Its active jobs, under its item number (with job data on): the jobs a new number would differ from.
    const stats = useItemStats(item.uuid, undefined, can(data, "stats"), false).data;
    const active = stats && !jobDataMissing(stats.data) ? activeJobs(stats.stats) : 0;
    // Typing another number says what it would affect, before it's saved (that it's unique: beside its label).
    const numberHint = (typed: string) => {
        if (!active) return undefined;
        if (itemNumberKey(typed) === itemNumberKey(item.item_number)) return `On ${activeJobsText(active)}.`;
        const one = active === 1;
        return (
            <span className="text-warning">
                On {activeJobsText(active)}. {one ? "It keeps" : "They keep"} &quot;{item.item_number}&quot; unless changed on {one ? "it" : "them"}, and your accounting
                software may create {one ? "a duplicate item" : "duplicate items"} once approved.
            </span>
        );
    };
    // Its details as saved, and as being changed (the draft: only the fields changed; null when none are).
    const saved = savedDetails(data, item, props.product);
    const d: Details = { ...saved, ...props.detailsDraft };
    const set = (patch: DetailsDraft) => {
        props.onDetailsDraft(changedDetails({ ...d, ...patch }, saved));
        return Promise.resolve();
    };
    // Its full name as ServiceM8 has it, or (its name or tags being changed) will once saved: its name with what its tags add.
    const own = data.itemTags[item.uuid] ?? [];
    const changing = d.name.trim() !== saved.name.trim() || (props.tagsDraft !== null && !sameTags(props.tagsDraft, own));
    const fullName = changing ? (smartName({ ...data, itemTags: { ...data.itemTags, [item.uuid]: props.tagsDraft ?? own } }, { ...item, name: d.name.trim() }) ?? d.name.trim()) : item.name;
    // Each card saved on its own (its save bar along its bottom, as its Pricing and Costing cards): its fields, and whether they're changed.
    const fields = detailFields(props.product);
    const cardChanged = (card: DetailsCard) => fields[card].some((f) => props.detailsDraft?.[f] !== undefined) || (card === "name" && changing);
    // A card's fields hold what's typed: started again on its Cancel and Save (the others' kept).
    const [versions, setVersions] = useState<Record<DetailsCard, number>>({ name: 0, number: 0, stock: 0 });
    const [nameBad, setNameBad] = useState(false);
    const [numberBad, setNumberBad] = useState(false);
    const [stockBad, setStockBad] = useState(false);
    const done = (card: DetailsCard) => {
        props.onDetailsDraft((was) => {
            if (!was) return was;
            const next = { ...was };
            for (const f of fields[card]) delete next[f];
            return Object.keys(next).length ? next : null;
        });
        if (card === "name") props.onTagsDraft(null);
        setVersions((v) => ({ ...v, [card]: v[card] + 1 }));
    };
    const saveBar = (card: DetailsCard, typedBad: boolean) =>
        cardChanged(card) ? (
            <div className="border-t border-brand-500/30 bg-brand-500/5 px-3 py-2">
                <DetailsSave
                    data={data}
                    item={item}
                    product={props.product}
                    card={card}
                    draft={props.detailsDraft}
                    tagsDraft={card === "name" ? props.tagsDraft : null}
                    active={active}
                    typedBad={typedBad}
                    onDone={() => done(card)}
                />
            </div>
        ) : undefined;
    const tint = (card: DetailsCard) => cardChanged(card) && "bg-brand-500/5";
    // Its stock (an item's: what ServiceM8 counts), said under its switch, short.
    const stockHow = d.tracked ? "Counted down as it's used on jobs." : "ServiceM8 doesn't count it.";
    // Built from products: the item number theirs make (as a bundle's items' do), offered under its field while it's another.
    const parts = props.product ? undefined : data.packages[item.uuid];
    const fromParts = (typed: string) => (parts && props.canEditNumber ? builtNumber(data.materials, parts, typed) : null);
    const useNumber = (number: string) => {
        void set({ item_number: number });
        // Its field shows it (it holds what's typed otherwise).
        setVersions((v) => ({ ...v, number: v.number + 1 }));
    };
    const hintFor = (typed: string) => {
        const suggested = fromParts(typed);
        const jobs = numberHint(typed);
        if (!suggested) return jobs;
        return (
            <>
                {jobs}
                <span className="block">
                    Its products make {suggested}.{" "}
                    <button type="button" className="link-button" onClick={() => useNumber(suggested)}>
                        Use it
                    </button>
                </span>
            </>
        );
    };
    const numberField = (
        <TextField
            label="Item number"
            value={d.item_number}
            disabled={!props.canEditNumber}
            live
            validate={props.product ? productNumberCheck : itemNumberCheck}
            // None, or another item's too (its pill in Needs attention, at the top of the window).
            warning={hasNumberIssue(data, item)}
            focusToken={itemNumberFocus}
            hint={hintFor}
            onSave={(item_number) => set({ item_number })}
        />
    );
    const barcodeField = <TextField label="Barcode" value={d.barcode} disabled={!canEdit} live onSave={(barcode) => set({ barcode })} />;
    return (
        <Box>
            {/* Drafts (each card's save bar, along its bottom, saves its own), so its fields don't say they're saved. */}
            <div className="space-y-4">
                {/* Its name, its tags and the full name they make in ServiceM8: one card (they make it together). Its name to update,
                    or its tags' clash, along its bottom (being changed: its save bar instead). */}
                <DraftFields key={`${item.uuid}:name:${versions.name}`} onInvalid={setNameBad}>
                    <SectionCard
                        label="Name"
                        bodyClassName={clsx("space-y-3.5 p-3", tint("name"))}
                        footer={saveBar("name", nameBad)}
                        fix={
                            <FixHere attached token={props.nameFixToken}>
                                {!cardChanged("name") && (props.tagsFix || props.nameNotice) ? (
                                    <>
                                        {props.tagsFix}
                                        {props.nameNotice}
                                    </>
                                ) : null}
                            </FixHere>
                        }
                    >
                        <TextField
                            label="Name"
                            hideLabel
                            value={d.name}
                            disabled={!canEdit}
                            live
                            validate={(v) => (v.trim() ? null : "Name is required")}
                            warning={props.nameShared !== undefined}
                            hint={props.nameShared ? <span className="text-warning">{props.nameShared}</span> : undefined}
                            focusToken={props.nameFocus}
                            onSave={(name) => set({ name })}
                        />
                        {/* Its tags (a product's are what it is: its name its own). Saved with the rest. */}
                        <TagPicker key={item.uuid} data={data} item={item} draft={props.tagsDraft} onDraft={props.onTagsDraft} savedWith="details" />
                        {/* Its name as ServiceM8 has it (what its tags add), or will once saved. */}
                        {props.product ? null : <NamePreview name={fullName} tags={data.nameTags} label={changing ? "Full name in ServiceM8, once saved" : "Full name in ServiceM8"} />}
                    </SectionCard>
                </DraftFields>
                {/* The codes it's known by: what accounting software matches it by (jobs keep the one they were given), and what's scanned. */}
                <DraftFields key={`${item.uuid}:number:${versions.number}`} onInvalid={setNumberBad}>
                    <SectionCard
                        label="Item number and barcode"
                        aside={props.product ? "Your own codes for it. Optional." : "Item numbers are unique across all items."}
                        bodyClassName={clsx("p-3", tint("number"))}
                        footer={saveBar("number", numberBad)}
                    >
                        <div className="grid items-start gap-4 sm:grid-cols-2">
                            {numberField}
                            {barcodeField}
                        </div>
                    </SectionCard>
                </DraftFields>
                {/* An item's stock, as ServiceM8's Stock tab has it: whether it's counted, and how many. */}
                {props.product ? null : (
                    <DraftFields key={`${item.uuid}:stock:${versions.stock}`} onInvalid={setStockBad}>
                        <SectionCard label="Stock" bodyClassName={clsx("p-3", tint("stock"))} footer={saveBar("stock", stockBad)}>
                            <div className="grid items-start gap-4 sm:grid-cols-2">
                                <div className="sm:pt-6">
                                    <ToggleField label="Track stock" checked={d.tracked} disabled={!canEdit} hint={stockHow} onSave={async (tracked) => set({ tracked })} />
                                </div>
                                <TextField
                                    label="In stock"
                                    value={d.inStock}
                                    inputMode="decimal"
                                    disabled={!canEdit || !d.tracked}
                                    live
                                    validate={(v) => (Number.isFinite(Number(v)) && v.trim() !== "" ? null : "Enter a number")}
                                    onSave={(inStock) => set({ inStock })}
                                />
                            </div>
                        </SectionCard>
                    </DraftFields>
                )}
            </div>
            <UsedIn data={data} item={item} token={props.usedInToken} />
            {/* Its price group (groups come with See costs, as its Pricing tab does). */}
            {/* Its pricing (and group) is on its Pricing tab; without See costs (no Pricing tab), its price here. */}
            {can(data, "costs.view") ? null : <PriceLine data={data} item={item} />}
            {/* No description, nor "use the description on invoices": the description is kept the same as the name (rules.ts save). */}
        </Box>
    );
}

/** Its details: its name (an item's without what its tags add; a product's whole), item number, barcode and stock. */
interface Details {
    name: string;
    item_number: string;
    barcode: string;
    /** Whether ServiceM8 counts its stock, and how many are in stock (as typed). */
    tracked: boolean;
    inStock: string;
}

/** Its details being changed: only those that differ from what's saved. */
export type DetailsDraft = Partial<Details>;

/** Its Details tab's cards, each saved on its own. */
type DetailsCard = "name" | "number" | "stock";

/** Each card's fields (its tags are its Name card's too). A product has no stock. */
const detailFields = (product: boolean): Record<DetailsCard, readonly (keyof Details)[]> => ({
    name: ["name"],
    number: ["item_number", "barcode"],
    stock: product ? [] : ["tracked", "inStock"],
});

/** Its details as saved. */
export const savedDetails = (data: PriceListData, item: MaterialRow, product: boolean): Details => ({
    name: product ? item.name : splitName(item.name, data.nameTags).base,
    item_number: item.item_number,
    barcode: item.barcode,
    tracked: item.item_is_inventoried,
    inStock: String(item.quantity_in_stock ?? 0),
});

function sameDetail(field: keyof Details, a: Details, b: Details) {
    if (field === "tracked") return a.tracked === b.tracked;
    if (field === "inStock") return Number(a.inStock) === Number(b.inStock);
    return a[field].trim() === b[field].trim();
}

/** What of `next` differs from what's saved (null: nothing). */
export function changedDetails(next: Details, saved: Details): DetailsDraft | null {
    const draft = Object.fromEntries((Object.keys(next) as (keyof Details)[]).filter((f) => !sameDetail(f, next, saved)).map((f) => [f, next[f]])) as DetailsDraft;
    return Object.keys(draft).length ? draft : null;
}

/**
 * One card of its Details tab being changed, saved: what changes on the left (its full name as ServiceM8 will have it, with its
 * tags' wording), Save and Cancel on the right; only its card's fields (the others' drafts wait for their own). An item's goes to
 * ServiceM8, so Save is held (as its price's Update); a product's is only kept here. Its tags are saved first (here), then the rest
 * in one ServiceM8 save.
 */
function DetailsSave(
    props: Readonly<{
        data: PriceListData;
        item: MaterialRow;
        product: boolean;
        card: DetailsCard;
        draft: DetailsDraft | null;
        /** Its tags being changed (its Name card's only). */
        tagsDraft: string[] | null;
        /** Its active jobs (a new item number differs from theirs). */
        active: number;
        typedBad: boolean;
        onDone: () => void;
    }>,
) {
    const { data, item, product } = props;
    const saver = usePriceListSaver();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const saved = savedDetails(data, item, product);
    // Its card's fields as being changed; the rest as saved.
    const mine = Object.fromEntries(detailFields(product)[props.card].flatMap((f) => (props.draft?.[f] === undefined ? [] : [[f, props.draft[f]]])));
    const d: Details = { ...saved, ...mine };
    const own = data.itemTags[item.uuid] ?? [];
    const tags = props.tagsDraft !== null && !sameTags(props.tagsDraft, own) ? props.tagsDraft : null;
    // Its name as ServiceM8 will have it: as it is, unless its name or tags are being changed (then its typed name, with what its
    // tags, as they'll be, add: never just because it doesn't read as its tags now: that's its own warning, Name to update). A
    // product's is its own.
    const nameMoves = d.name.trim() !== saved.name.trim() || tags !== null;
    const composed = smartName({ ...data, itemTags: { ...data.itemTags, [item.uuid]: tags ?? own } }, { ...item, name: d.name.trim() }) ?? d.name.trim();
    const name = product ? d.name.trim() : nameMoves ? composed : item.name;
    const numberMoves = d.item_number.trim() !== saved.item_number.trim();
    const barcodeMoves = d.barcode.trim() !== saved.barcode.trim();
    // Its tags changed, by set (a pick-one set's swap on one line): "Scope: Installation → Programming".
    const tagNames = (ids: readonly string[]) => data.tags.filter((t) => ids.includes(t.id));
    const added = tags ? tagNames(tags.filter((id) => !own.includes(id))) : [];
    const removed = tags ? tagNames(own.filter((id) => !tags.includes(id))) : [];
    const tagLines = [...new Set([...added, ...removed].map((t) => t.setId ?? ""))].map((setId) => {
        const names = (list: readonly TagRow[]) =>
            list
                .filter((t) => (t.setId ?? "") === setId)
                .map((t) => t.name)
                .join(", ") || "none";
        return { what: data.tagSets.find((s) => s.id === setId)?.name ?? "Tags", from: names(removed), to: names(added) };
    });
    // What's said (the full name it gives is in the preview above): the name as typed (an item's: without its tags' wording).
    const changes = [
        d.name.trim() !== saved.name.trim() ? { what: "Name", from: saved.name.split(/\r?\n/)[0] ?? "", to: d.name.trim().split(/\r?\n/)[0] ?? "" } : null,
        ...tagLines,
        numberMoves ? { what: "Item number", from: item.item_number || "none", to: d.item_number.trim() || "none" } : null,
        barcodeMoves ? { what: "Barcode", from: item.barcode || "none", to: d.barcode.trim() || "none" } : null,
        d.tracked !== item.item_is_inventoried ? { what: "Track stock", from: item.item_is_inventoried ? "on" : "off", to: d.tracked ? "on" : "off" } : null,
        Number(d.inStock) !== Number(item.quantity_in_stock ?? 0) ? { what: "In stock", from: String(item.quantity_in_stock ?? 0), to: String(Number(d.inStock)) } : null,
    ].filter((c) => c !== null);
    const one = props.active === 1;
    const save = async () => {
        setBusy(true);
        setError(null);
        try {
            // Its tags changed: saved with the whole name as shown (their wording too), together, so it never reads as not matching
            // them in between. Else the rest in one save (a change to its base alone keeps the wording it has).
            if (tags) await saver.setItemTags(item.uuid, tags, false, !product && name !== item.name ? name : undefined);
            const patch: Parameters<typeof saver.updateMaterial>[1] = {};
            if (product ? d.name.trim() !== item.name : !tags && name !== item.name) Object.assign(patch, product ? { name: d.name.trim() } : { nameChange: { base: d.name.trim() } });
            if (numberMoves) patch.item_number = d.item_number.trim();
            if (barcodeMoves) patch.barcode = d.barcode.trim();
            if (d.tracked !== item.item_is_inventoried) patch.item_is_inventoried = d.tracked;
            if (Number(d.inStock) !== Number(item.quantity_in_stock ?? 0)) patch.quantity_in_stock = Number(d.inStock);
            if (Object.keys(patch).length) await saver.updateMaterial(item.uuid, patch);
            props.onDone();
        } catch (err) {
            setError(err as Error);
        } finally {
            setBusy(false);
        }
    };
    const label = product ? "Save" : "Save to ServiceM8";
    return (
        // Along the bottom of its card (as its Pricing and Costing cards' save bars): what saving does on the left, its buttons on the right.
        <DraftBar
            message={
                <>
                    {changes.map((c) => (
                        <span key={c.what} className="block">
                            {c.what}: {c.from} → <span className="font-semibold text-ink">{c.to}</span>
                        </span>
                    ))}
                    {numberMoves && props.active && item.item_number ? (
                        <span className="block text-xs text-warning">
                            On {activeJobsText(props.active)}: {one ? "it keeps" : "they keep"} {item.item_number} unless changed on {one ? "it" : "them"}, and your accounting software may
                            create {one ? "a duplicate item" : "duplicate items"} once approved.
                        </span>
                    ) : null}
                    {props.typedBad ? <span className="block text-xs text-danger">Put right what&apos;s marked above to save.</span> : null}
                </>
            }
            busy={busy}
            error={error}
            actions={
                <>
                    {product ? (
                        <button type="button" className="btn-primary" disabled={busy || props.typedBad} onClick={() => void save()}>
                            {label}
                        </button>
                    ) : (
                        <HoldButton className="btn-primary" label={label} disabled={busy || props.typedBad} title="Its details, saved to ServiceM8" onDone={() => void save()} />
                    )}
                    <button type="button" className="btn-secondary" disabled={busy} onClick={props.onDone}>
                        Cancel
                    </button>
                </>
            }
        />
    );
}
